/**
 * SEO utility for Vue Meta
 */

/**
 * Creates a metaInfo object for vue-meta based on provided SEO configuration
 * @param {Object} seo - SEO configuration object
 * @param {string} seo.title - Page title
 * @param {string} seo.description - Page description
 * @param {string} seo.keywords - Comma-separated keywords
 * @param {string} seo.ogImage - Open Graph image URL
 * @param {string} seo.ogType - Open Graph type (default: website)
 * @param {string} seo.twitterCard - Twitter card type (default: summary_large_image)
 * @returns {Object} metaInfo object for vue-meta
 */
export function createMetaInfo(seo) {
  if (!seo) return {};

  // Default values
  const defaults = {
    title: 'SeleAI - Tu Tutor de IA para Estudios',
    description: 'Transforma tus apuntes en un tutor de IA interactivo. Crea cuestionarios, resúmenes y flashcards para mejorar tu aprendizaje.',
    keywords: 'tutor ia, estudio inteligente, cuestionarios ia, resúmenes ia, flashcards ia, aprendizaje personalizado',
    ogImage: 'https://sele.ai/og-image.jpg',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    baseUrl: 'https://sele.ai'
  };

  // Merge provided SEO config with defaults
  const config = { ...defaults, ...seo };
  
  // Build page URL
  const pageUrl = typeof window !== 'undefined' 
    ? `${config.baseUrl}${window.location.pathname}`
    : config.baseUrl;
  
  // Create metaInfo object for vue-meta
  return {
    title: config.title,
    titleTemplate: '%s',
    meta: [
      { name: 'description', content: config.description },
      { name: 'keywords', content: config.keywords },
      
      // Open Graph
      { property: 'og:title', content: config.title },
      { property: 'og:description', content: config.description },
      { property: 'og:image', content: config.ogImage },
      { property: 'og:url', content: pageUrl },
      { property: 'og:type', content: config.ogType },
      
      // Twitter
      { property: 'twitter:title', content: config.title },
      { property: 'twitter:description', content: config.description },
      { property: 'twitter:image', content: config.ogImage },
      { property: 'twitter:url', content: pageUrl },
      { property: 'twitter:card', content: config.twitterCard }
    ]
  };
}

/**
 * SEO configurations for different routes
 */
export const seoConfigs = {
  // Homepage
  home: {
    title: 'SeleAI - Tu Tutor de IA para Estudios',
    description: 'Transforma tus apuntes en un tutor de IA interactivo. Crea cuestionarios, resúmenes y flashcards para mejorar tu aprendizaje.',
    keywords: 'tutor ia, estudio inteligente, cuestionarios ia, resúmenes ia, flashcards ia, aprendizaje personalizado'
  },
  
  // Bulk Mockup Generator - Convertido a Generador de Cuestionarios
  bulkMockup: {
    title: 'Generador de Cuestionarios | Procesa tus Apuntes - SeleAI',
    description: 'Sube tus apuntes y crea cuestionarios personalizados en segundos. Mejora tu aprendizaje con preguntas generadas por IA.',
    keywords: 'generador de cuestionarios, preguntas ia, estudio inteligente, apuntes, exámenes'
  },
  
  // Login
  login: {
    title: 'Inicia Sesión en tu Cuenta - SeleAI',
    description: 'Accede a tu cuenta de SeleAI para crear y gestionar tus materiales de estudio. Inicia sesión para guardar tus documentos y acceder a funciones premium.',
    keywords: 'seleai login, iniciar sesión, cuenta de estudio, acceso usuario'
  },
  
  // Premium
  premium: {
    title: 'Membresía Premium - Desbloquea Funciones Avanzadas | SeleAI',
    description: 'Actualiza a SeleAI Premium para obtener cuestionarios ilimitados, resúmenes más detallados y opciones de personalización avanzadas. Maximiza tu aprendizaje.',
    keywords: 'premium estudio, cuestionarios ilimitados, funciones avanzadas, resúmenes personalizados, suscripción estudio'
  },
  
  // Blog
  blog: {
    title: 'Consejos de Estudio y Técnicas de Aprendizaje | Blog SeleAI',
    description: 'Aprende cómo mejorar tus técnicas de estudio, optimizar tu tiempo de aprendizaje y aumentar tu rendimiento académico con nuestros consejos expertos.',
    keywords: 'consejos de estudio, técnicas de aprendizaje, blog educativo, tutoriales estudio, métodos de memorización, rendimiento académico'
  },
  
  // Contact
  contact: {
    title: 'Contacto - Soporte SeleAI',
    description: '¿Tienes preguntas sobre SeleAI? Ponte en contacto con nuestro equipo para recibir asistencia con tus materiales de estudio o dudas sobre tu cuenta.',
    keywords: 'contacto seleai, soporte estudio, ayuda aprendizaje, asistencia usuario'
  },
  
  // 404
  notFound: {
    title: 'Página No Encontrada - SeleAI',
    description: 'La página que estás buscando no se puede encontrar. Vuelve a SeleAI para crear materiales de estudio inteligentes.',
    keywords: 'seleai, página no encontrada'
  }
};

export default { createMetaInfo, seoConfigs }; 